// Walkthrough numbers for the MCO Calculator tab — same engine as stress/compare,
// shaped like the Rogers calculator sections (no plan picker, no narrative copy).

import { computeMcoProjectedPayment, roundHalfUp } from "./mcoModel.js";
import { num, toShareDecimal } from "./mcoShared.js";
import { stressedHarvestCosts } from "./mcoStressGrid.js";

const round2 = (v) => roundHalfUp(v, 2);

/**
 * @param {object} baseline — stressBaseline from useMco
 * @param {object} stress — { harvestPrice, yieldPct, costPct } (pct as 0–1 fractions of expected)
 * @param {object} [premium] — computeMcoProducerPremium result wrapper ({ ready, result })
 * @param {object} [opts] — { practiceName }
 */
export function buildMcoCalculatorWalk(baseline, stress, premium, opts = {}) {
  if (!baseline) {
    return { ready: false, blockers: ["baseline missing"] };
  }

  const eay = num(baseline.expectedYield);
  const pp = num(baseline.projectedPrice);
  const er = baseline.expectedRevenue == null ? null : num(baseline.expectedRevenue);
  const em = baseline.expectedMargin == null ? null : num(baseline.expectedMargin);
  const tmOfficial =
    baseline.officialTriggerMargin == null ? null : num(baseline.officialTriggerMargin);
  const ecost = num(baseline.projectedCosts);
  const cr = num(
    baseline.coverageRange ??
      round2(num(baseline.coverageStart ?? 0.95) - num(baseline.areaLossEnd ?? 0.86))
  );
  const tl = num(baseline.coverageStart ?? 0.95);
  const aph = num(baseline.approvedYield);
  const acres = num(baseline.acres);
  const share = toShareDecimal(baseline.sharePct);
  const protectionFactor = num(baseline.protectionFactor ?? 1);
  const planCode = String(baseline.planCode || "68");

  const hp = num(stress?.harvestPrice ?? pp);
  const ypct = num(stress?.yieldPct ?? 1);
  const cpct = num(stress?.costPct ?? 1);
  const finalYield = roundHalfUp(eay * ypct, 1);
  const harvestCosts = round2(stressedHarvestCosts(baseline, cpct));

  const proj = computeMcoProjectedPayment({
    ...baseline,
    finalYield,
    harvestPrice: hp,
    harvestCosts,
  });
  if (!proj.ready) {
    return { ready: false, blockers: proj.blockers || ["payment unavailable"] };
  }

  const r = proj.result;
  const ecostShown = Number.isFinite(ecost) && ecost !== 0 ? ecost : r.expectedCosts;
  const ded = er != null && tmOfficial != null ? round2(em - tmOfficial) : round2(er != null ? er * (1 - tl) : 0);
  const tm = tmOfficial != null && Number.isFinite(tmOfficial) ? tmOfficial : r.triggerMargin;
  const cv = r.coverageValue;

  // Section 3 protection at projected price (September lock); one round on the full product
  // (Rogers calculator.js). Payment protection may recompute at max(PP, HP) for plan 68.
  const prot0 = Math.round(aph * pp * acres * share * cr * protectionFactor);
  const protU = Math.round(aph * Math.max(pp, planCode === "68" ? hp : pp) * acres * share * cr * protectionFactor);
  const check = Math.round(r.paymentFactor * protU);

  const recomputed = planCode === "68" && hp > pp;
  const rate = premium?.ready ? premium.result?.baseRate : null;
  const hasRate = rate != null && Number.isFinite(rate);
  const subsidy = premium?.ready ? num(premium.result?.subsidyPercent ?? 0.8) : 0.8;

  return {
    ready: true,
    blockers: [],
    unit: {
      practiceName: opts.practiceName || "Unit",
      aph,
      acres,
      share,
      coveragePercent: protectionFactor,
    },
    county: {
      eay,
      pp,
      er: er != null ? round2(er) : r.expectedRevenue,
      em: em != null ? round2(em) : r.expectedMargin,
      ecost: round2(ecostShown),
      tm: round2(tm),
      cv: round2(cv),
      cr,
      tl,
      ded,
      dedPct: Math.round((1 - tl) * 100),
      estimates: baseline.expectedYieldBasis !== "official",
    },
    stress: { harvestPrice: hp, yieldPct: ypct, costPct: cpct, finalYield, harvestCosts },
    protection: { prot0 },
    premium: hasRate
      ? {
          hasRate: true,
          rate,
          total: premium.result.totalPremiumAmount,
          gov: premium.result.subsidyAmount,
          you: premium.result.producerPremiumAmount,
          subsidy,
        }
      : { hasRate: false, subsidy },
    harvest: {
      revenue: r.harvestRevenue,
      margin: r.harvestMargin,
      usePrice: planCode === "67" ? pp : r.harvestPrice,
      recomputed,
      recompute: recomputed
        ? {
            er2: round2(eay * hp),
            tm2: r.triggerMargin,
            protU,
          }
        : null,
    },
    payment: {
      trigger: r.triggerMargin,
      margin: r.harvestMargin,
      short: r.deficient,
      cover: r.coverageValue,
      pf: r.paymentFactor,
      protU,
      check,
    },
    totals: {
      protection: prot0,
      youPremium: hasRate ? premium.result.producerPremiumAmount : null,
      pf: r.paymentFactor,
      check,
    },
  };
}
